/* global __DATA_HUB_BUILD_DEFAULT_WEB_SETTINGS_JSON__ */

export const default_web_analytics_settings = {
    enabled: true,
    posthog_listen: false,
    posthog_publish: false,
    // Exponential-with-cap ping cadence: each entry is the delay (ms) before the next ping.
    // The last value repeats forever — so [10s, 20s, 40s, 80s, 120s] sends pings at cumulative
    // t=10s, 30s, 70s, 150s, 270s, then every 120s indefinitely. Tunable per-org via web_settings.
    ping_schedule_ms: [10000, 20000, 40000, 80000, 120000],
    flush_interval_ms: 5000,
    batch_size: 8,
    max_pre_init_queue_size: 30,
    max_persisted_events: 30,
    max_persisted_bytes: 32 * 1024,
    max_beacon_payload_bytes: 60 * 1024,
    event_name_rate_limit_per_minute: 120,
    spa_pageview: true,
    auto_pageview: true,
    auto_click: true,
    auto_scroll: true,
    auto_ping: true,
    auto_page_leave: true,
    auto_performance: true,
    auto_youtube: false,
    auto_iframe_click: true,
    capture_early_auto_events: true,
    scroll_thresholds: [25, 50, 75, 100],
    mobile_touch_click_guard: true,
    mobile_touch_click_max_movement_px: 12,
    mobile_click_after_touch_window_ms: 700,
    mobile_scroll_depth_guard: true,
    mobile_scroll_min_depth_delta_px: 48,
    mobile_scroll_require_scrollable_page: true,
    mobile_scroll_min_page_overflow_px: 8,
    video_max_elements_tracked: 5,
    video_progress_thresholds: [25, 50, 75],
    video_poll_interval_ms: 1000,
    idle_after_ms: 15000
};

/**
 * Per-environment web-settings overrides baked into the bundle by webpack DefinePlugin from the
 * DATA_HUB_DEFAULT_WEB_SETTINGS_JSON env var (see deploy/build-envs/*.env). The generic
 * data-hub-admin build sets nothing → this returns {} and the generic defaults above apply. An
 * org build (e.g. daily-kos-beta) can bake toggles like {"auto_iframe_click": true}. Guarded so
 * non-webpack contexts (vitest / node) where the global is undefined fall back to {}, and tolerant
 * of an empty or malformed value (the build already validates it; this is a runtime safety net).
 *
 * @returns {Object} Parsed override object, or {} when unset/invalid.
 */
export function get_build_default_web_settings() {
    try {
        if (typeof __DATA_HUB_BUILD_DEFAULT_WEB_SETTINGS_JSON__ === 'undefined') {
            return {};
        }
        const raw_web_settings_json = __DATA_HUB_BUILD_DEFAULT_WEB_SETTINGS_JSON__;
        if (!raw_web_settings_json || typeof raw_web_settings_json !== 'string') {
            return {};
        }
        const parsed_web_settings = JSON.parse(raw_web_settings_json);
        return parsed_web_settings && typeof parsed_web_settings === 'object' && !Array.isArray(parsed_web_settings)
            ? parsed_web_settings
            : {};
    } catch {
        return {};
    }
}

function coerce_boolean_setting(value, fallback_value) {
    if (typeof value === 'boolean') {
        return value;
    }
    if (typeof value === 'string') {
        const normalized_value = value.trim().toLowerCase();
        if (['true', '1', 'yes', 'on'].includes(normalized_value)) {
            return true;
        }
        if (['false', '0', 'no', 'off'].includes(normalized_value)) {
            return false;
        }
    }
    return fallback_value;
}

function coerce_positive_number_setting(value, fallback_value) {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
        return value;
    }
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed_value = Number(value);
        if (Number.isFinite(parsed_value) && parsed_value > 0) {
            return parsed_value;
        }
    }
    return fallback_value;
}

// Same as coerce_positive_number_setting but accepts 0. Used for "minimum" thresholds where
// 0 is a meaningful value (= "no minimum, always emit"). The runtime sites that consume these
// also accept >= 0, so the coerce contract matches what the consumer treats as valid.
function coerce_non_negative_number_setting(value, fallback_value) {
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
        return value;
    }
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed_value = Number(value);
        if (Number.isFinite(parsed_value) && parsed_value >= 0) {
            return parsed_value;
        }
    }
    return fallback_value;
}

function coerce_threshold_array(value, fallback_value) {
    const source_values = Array.isArray(value) ? value : fallback_value;
    const normalized_values = source_values
        .map((threshold_value) => Number(threshold_value))
        .filter((threshold_value) => Number.isFinite(threshold_value) && threshold_value > 0 && threshold_value <= 100)
        .map((threshold_value) => Math.round(threshold_value));
    return Array.from(new Set(normalized_values)).sort((first_value, second_value) => first_value - second_value);
}

// Like coerce_threshold_array but preserves order and allows duplicates (the ping schedule
// uses position semantics — entry[0] is the first delay, last entry repeats indefinitely).
// Falls back to defaults when the override is empty or has no valid positive numbers.
function coerce_positive_number_array(value, fallback_value) {
    const source_values = Array.isArray(value) ? value : fallback_value;
    const normalized_values = source_values
        .map((entry_value) => Number(entry_value))
        .filter((entry_value) => Number.isFinite(entry_value) && entry_value > 0)
        .map((entry_value) => Math.round(entry_value));
    if (normalized_values.length > 0) {
        return normalized_values;
    }
    return fallback_value.slice();
}

/**
 * Builds the final web analytics settings object from defaults, instance-level settings,
 * and constructor config. This is intentionally defensive because settings can come from
 * JavaScript init code, data attributes, organization metadata, or later experiments.
 *
 * @param {Object} config - DataHub constructor config. May contain a web_settings object.
 * @param {Object} existing_settings - Existing settings already attached to the instance.
 * @returns {Object} Normalized web analytics settings.
 */
export function build_web_analytics_settings(config = {}, existing_settings = {}) {
    const configured_settings = config && typeof config === 'object' && config.web_settings && typeof config.web_settings === 'object'
        ? config.web_settings
        : {};
    // Precedence (lowest → highest): generic defaults → per-environment baked overrides →
    // instance settings → explicit runtime config. Baked overrides let an org build change a
    // default (e.g. auto_iframe_click) while runtime config.web_settings can still override it.
    const merged_settings = {
        ...default_web_analytics_settings,
        ...get_build_default_web_settings(),
        ...(existing_settings && typeof existing_settings === 'object' ? existing_settings : {}),
        ...configured_settings
    };

    return {
        enabled: coerce_boolean_setting(merged_settings.enabled, default_web_analytics_settings.enabled),
        posthog_listen: coerce_boolean_setting(merged_settings.posthog_listen, default_web_analytics_settings.posthog_listen),
        posthog_publish: coerce_boolean_setting(merged_settings.posthog_publish, default_web_analytics_settings.posthog_publish),
        spa_pageview: coerce_boolean_setting(merged_settings.spa_pageview, default_web_analytics_settings.spa_pageview),
        auto_pageview: coerce_boolean_setting(merged_settings.auto_pageview, default_web_analytics_settings.auto_pageview),
        auto_click: coerce_boolean_setting(merged_settings.auto_click, default_web_analytics_settings.auto_click),
        auto_scroll: coerce_boolean_setting(merged_settings.auto_scroll, default_web_analytics_settings.auto_scroll),
        auto_ping: coerce_boolean_setting(merged_settings.auto_ping, default_web_analytics_settings.auto_ping),
        auto_page_leave: coerce_boolean_setting(merged_settings.auto_page_leave, default_web_analytics_settings.auto_page_leave),
        auto_performance: coerce_boolean_setting(merged_settings.auto_performance, default_web_analytics_settings.auto_performance),
        auto_youtube: coerce_boolean_setting(merged_settings.auto_youtube, default_web_analytics_settings.auto_youtube),
        auto_iframe_click: coerce_boolean_setting(merged_settings.auto_iframe_click, default_web_analytics_settings.auto_iframe_click),
        capture_early_auto_events: coerce_boolean_setting(merged_settings.capture_early_auto_events, default_web_analytics_settings.capture_early_auto_events),
        ping_schedule_ms: coerce_positive_number_array(merged_settings.ping_schedule_ms, default_web_analytics_settings.ping_schedule_ms),
        flush_interval_ms: coerce_positive_number_setting(merged_settings.flush_interval_ms, default_web_analytics_settings.flush_interval_ms),
        batch_size: coerce_positive_number_setting(merged_settings.batch_size, default_web_analytics_settings.batch_size),
        max_pre_init_queue_size: coerce_positive_number_setting(merged_settings.max_pre_init_queue_size, default_web_analytics_settings.max_pre_init_queue_size),
        max_persisted_events: coerce_positive_number_setting(merged_settings.max_persisted_events, default_web_analytics_settings.max_persisted_events),
        max_persisted_bytes: coerce_positive_number_setting(merged_settings.max_persisted_bytes, default_web_analytics_settings.max_persisted_bytes),
        max_beacon_payload_bytes: coerce_positive_number_setting(merged_settings.max_beacon_payload_bytes, default_web_analytics_settings.max_beacon_payload_bytes),
        event_name_rate_limit_per_minute: coerce_positive_number_setting(merged_settings.event_name_rate_limit_per_minute, default_web_analytics_settings.event_name_rate_limit_per_minute),
        video_poll_interval_ms: coerce_positive_number_setting(merged_settings.video_poll_interval_ms, default_web_analytics_settings.video_poll_interval_ms),
        video_max_elements_tracked: coerce_positive_number_setting(merged_settings.video_max_elements_tracked, default_web_analytics_settings.video_max_elements_tracked),
        idle_after_ms: coerce_positive_number_setting(merged_settings.idle_after_ms, default_web_analytics_settings.idle_after_ms),
        scroll_thresholds: coerce_threshold_array(merged_settings.scroll_thresholds, default_web_analytics_settings.scroll_thresholds),
        video_progress_thresholds: coerce_threshold_array(merged_settings.video_progress_thresholds, default_web_analytics_settings.video_progress_thresholds),
        mobile_touch_click_guard: coerce_boolean_setting(
            merged_settings.mobile_touch_click_guard,
            default_web_analytics_settings.mobile_touch_click_guard
        ),
        mobile_scroll_depth_guard: coerce_boolean_setting(
            merged_settings.mobile_scroll_depth_guard,
            default_web_analytics_settings.mobile_scroll_depth_guard
        ),
        mobile_scroll_require_scrollable_page: coerce_boolean_setting(
            merged_settings.mobile_scroll_require_scrollable_page,
            default_web_analytics_settings.mobile_scroll_require_scrollable_page
        ),
        mobile_touch_click_max_movement_px: coerce_positive_number_setting(
            merged_settings.mobile_touch_click_max_movement_px,
            default_web_analytics_settings.mobile_touch_click_max_movement_px
        ),
        mobile_click_after_touch_window_ms: coerce_positive_number_setting(
            merged_settings.mobile_click_after_touch_window_ms,
            default_web_analytics_settings.mobile_click_after_touch_window_ms
        ),
        mobile_scroll_min_depth_delta_px: coerce_non_negative_number_setting(
            merged_settings.mobile_scroll_min_depth_delta_px,
            default_web_analytics_settings.mobile_scroll_min_depth_delta_px
        ),
        mobile_scroll_min_page_overflow_px: coerce_non_negative_number_setting(
            merged_settings.mobile_scroll_min_page_overflow_px,
            default_web_analytics_settings.mobile_scroll_min_page_overflow_px
        )
    };
}
